import { config } from "../Environment";

// ── Types ──────────────────────────────────────────────────────────────────

export interface SegmentSignupLink {
  token: string | null;
  enabled: boolean;
  expiresAt: string | null;
  maxUses: number | null;
  usesCount: number;
}

export interface SegmentMember {
  _id: string;
  name: string;
  email: string;
  joinedAt: string | null;
  isActive: boolean;
  /** True when this segment was the client's first-touch attribution. */
  attributedHere: boolean;
}

export interface PricingTier {
  untilCount: number | null; // null = "and beyond"
  amount: number;
}

export interface SegmentPricing {
  enabled: boolean;
  fiProduct: string | null;
  // Present on getSegment (detailed) responses:
  amount?: number;
  currency?: string;
  tiers?: PricingTier[];
  currentAmount?: number;
  paidCount?: number;
}

export interface MentorSegment {
  _id: string;
  name: string;
  description: string;
  kind: "mentor_segment";
  isActive: boolean;
  fiOnboardingProgram: string | null;
  defaultRelationshipType: string | null;
  pricing: SegmentPricing;
  memberCount: number;
  signupLink: SegmentSignupLink;
  /** Automatic AI-credit charge-up for new members (granted once per user) */
  creditGrant: { enabled: boolean; amount: number };
  members?: SegmentMember[];
}

export interface SegmentPublicInfo {
  segment: {
    _id: string;
    name: string;
    description: string;
    hasOnboarding: boolean;
  };
  expert: {
    _id: string;
    name: string;
  };
  pricing?: {
    priced: boolean;
    amount?: number;
    currency?: string;
    productId?: string;
  };
}

export interface UpdateSegmentPricingRequest {
  enabled: boolean;
  amount?: number;
  currency?: string;
  tiers?: PricingTier[];
}

export interface UpdateSegmentPricingResponse {
  segment: MentorSegment;
  pricing?: {
    enabled: boolean;
    productId: string;
    amount: number;
    currency: string;
    tiers: PricingTier[];
    currentAmount: number;
    paidCount: number;
  };
}

export interface CreateSegmentRequest {
  name: string;
  description?: string;
  fiOnboardingProgram?: string | null;
  defaultRelationshipType?: string | null;
  creditGrant?: { enabled: boolean; amount: number };
}

export interface UpdateSegmentRequest {
  name?: string;
  description?: string;
  fiOnboardingProgram?: string | null;
  defaultRelationshipType?: string | null;
  isActive?: boolean;
  signupLink?: {
    enabled?: boolean;
    expiresAt?: string | null;
    maxUses?: number | null;
  };
  creditGrant?: { enabled: boolean; amount: number };
}

// ── Owner (expert) endpoints ────────────────────────────────────────────────

export const listSegments = async (
  retryFetch: Function
): Promise<{ segments: MentorSegment[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/mentor/segments`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list segments: ${res.statusText}`);
  return res.json();
};

export const getSegment = async (
  retryFetch: Function,
  id: string
): Promise<{ segment: MentorSegment }> => {
  const res = await retryFetch(`${config.API_BASE}/api/mentor/segments/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to get segment: ${res.statusText}`);
  return res.json();
};

export const createSegment = async (
  retryFetch: Function,
  data: CreateSegmentRequest
): Promise<{ segment: MentorSegment }> => {
  const res = await retryFetch(`${config.API_BASE}/api/mentor/segments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create segment: ${res.statusText}`);
  }
  return res.json();
};

export const updateSegment = async (
  retryFetch: Function,
  id: string,
  data: UpdateSegmentRequest
): Promise<{ segment: MentorSegment }> => {
  const res = await retryFetch(`${config.API_BASE}/api/mentor/segments/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update segment: ${res.statusText}`);
  }
  return res.json();
};

export const rotateSegmentToken = async (
  retryFetch: Function,
  id: string
): Promise<{ segment: MentorSegment }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/mentor/segments/${id}/token/rotate`,
    { method: "POST", credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to rotate token: ${res.statusText}`);
  return res.json();
};

export const updateSegmentPricing = async (
  retryFetch: Function,
  id: string,
  data: UpdateSegmentPricingRequest
): Promise<UpdateSegmentPricingResponse> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/mentor/segments/${id}/pricing`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // Surface the structured code so the UI can react (e.g. needs_vendor).
    const e: any = new Error(err.error || `Failed to update pricing: ${res.statusText}`);
    e.code = err.code;
    throw e;
  }
  return res.json();
};

/**
 * Owner — add an existing user to a segment by email. Free regardless of
 * pricing; the member immediately gets the mentor relationship and the
 * segment's onboarding program. Throws with `code: "user_not_found"` when
 * no account exists for that email.
 */
export const addSegmentMember = async (
  retryFetch: Function,
  id: string,
  email: string
): Promise<{
  ok: boolean;
  alreadyMember: boolean;
  onboardingProgramId: string | null;
  member: { _id: string; name: string; email: string };
  segment: MentorSegment;
}> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/mentor/segments/${id}/members`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e: any = new Error(err.error || `Failed to add member: ${res.statusText}`);
    e.code = err.code;
    throw e;
  }
  return res.json();
};

export const deleteSegment = async (
  retryFetch: Function,
  id: string
): Promise<{ ok: boolean }> => {
  const res = await retryFetch(`${config.API_BASE}/api/mentor/segments/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to delete segment: ${res.statusText}`);
  return res.json();
};

// ── Public / join endpoints ─────────────────────────────────────────────────

/** Public — no auth. Powers the /join-mentor/:token landing page. */
export const getSegmentPublicInfo = async (
  token: string
): Promise<SegmentPublicInfo> => {
  const res = await fetch(`${config.API_BASE}/api/segment/info/${token}`);
  if (!res.ok) throw new Error(`Invalid or inactive link`);
  return res.json();
};

/** Authenticated — join a segment via its referral token. */
export const joinSegment = async (
  retryFetch: Function,
  token: string
): Promise<{
  ok: boolean;
  alreadyMember: boolean;
  onboardingProgramId: string | null;
  segment: { _id: string; name: string };
  expertId: string;
}> => {
  const res = await retryFetch(`${config.API_BASE}/api/segment/join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to join: ${res.statusText}`);
  }
  return res.json();
};

/** Public — guest checkout for a paid segment (no auth). Returns a checkout redirect URL. */
export const guestCheckout = async (
  token: string,
  email: string,
  name: string
): Promise<{ redirect: string; email: string }> => {
  const res = await fetch(`${config.API_BASE}/api/segment/checkout/guest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, email, name }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e: any = new Error(data.error || "Checkout failed");
    e.code = data.code;
    throw e;
  }
  return data;
};

/** Build the shareable public URL for a segment token. */
export const buildSegmentSignupUrl = (token: string): string => {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/join-mentor/${token}`;
};
